import { useCallback } from 'react';

import { useI18n } from '@change-corgi/core/react/i18n';
import { useTracking } from '@change-corgi/core/react/tracking';

import { useAutoCheckOffTask } from 'src/app/pages/starterDashboardV2/shared/hooks';
import { getFile } from 'src/app/shared/hooks/file';
import { shareNativeExtended } from 'src/app/shared/share/mobileNativeShare';
import { copyToClipboard } from 'src/app/shared/utils/clipboard';

import { useShareUrlWithUtmParams } from '../../utils/getShareUrlWithUtmParams';
import { replaceUrlInMessage } from '../../utils/replaceUrlInMessage';
import { stripQuery } from '../../utils/stripQuery';

import type { ActionsReturnType, PostSharingHookProps } from './types';

const TRACKING_EVENT_NAME = 'share_petition';
// The following is not required by product but required by the native share utility
const SHARING_EVENT_PREFIX = 'native_share_ready_to_share_instagram_post';
export function useInstagramPostSharing(props: PostSharingHookProps): ActionsReturnType {
	const track = useTracking();
	const i18n = useI18n();
	const autoCheckOffTask = useAutoCheckOffTask();

	const {
		previewImageUrl,
		message,
		shareUrl = '',
		trackingProps: { context, viewContext },
	} = props;

	const shareUrlWithUtmParams = useShareUrlWithUtmParams(shareUrl, 'instagram_post');

	const primaryAction = useCallback(async () => {
		const messageToCopy = replaceUrlInMessage(message, shareUrl, shareUrlWithUtmParams || shareUrl);
		void copyToClipboard(messageToCopy, i18n);
		const image = await getFile(stripQuery(previewImageUrl));
		const trackingData = {
			context,
			location: 'instagram_post',
			tab_at_share: viewContext,
			content: 'post',
			app_available: false,
		};

		if (!image) {
			return;
		}

		void track(TRACKING_EVENT_NAME, trackingData);
		await shareNativeExtended(
			{ files: [image] },
			{
				trackingEvents: { success: `${SHARING_EVENT_PREFIX}_success`, failure: `${SHARING_EVENT_PREFIX}_failure` },
				trackingData,
			},
			track,
		);
		await autoCheckOffTask('share_instagram');
	}, [context, i18n, previewImageUrl, message, shareUrl, shareUrlWithUtmParams, track, viewContext, autoCheckOffTask]);

	return {
		primaryAction,
		secondaryAction: undefined,
	};
}
